import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useData } from './DataContext';
import { apiGet, handleApiError, setGlobalUserId  } from './ApiClient';

type ProtectedRouteProps = {
  children: React.ReactNode;
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { refreshData } = useData();
  const [organizationName, setOrganizationName] = useState<string>('');
  const accessToken = auth.user?.access_token;
  const profile = auth.user?.profile;
  const organizationId = profile?.['custom:organization_id'];
  
  const signOutRedirect = () => {
    try {
      // Cognitoのログアウトエンドポイントにリダイレクト
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || "6gghtveb1f49aj61fg65rnu4dv";
      const logoutUri = encodeURIComponent(process.env.REACT_APP_COGNITO_LOGOUT_URI || "https://www.gxp21.com/logout");
      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || "https://ap-northeast-11rz7rytqe.auth.ap-northeast-1.amazoncognito.com";

      // ローカルストレージもクリア
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
    } catch (error) {
      console.error('サインアウトエラー:', error);
      // エラーが発生してもログアウトページに遷移
      window.location.href = '/logout';
    }
  };
  
  // 認証成功後に一度だけデータを初期化
  useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading) {
      let userName = profile?.name ? profile.name : '';
      if (userName === 'register') { //アカウント登録用の専用ユーザーですアプリをアクセスできない
        signOutRedirect();
        return;
      }

      // 認証成功後、保存されていた元のURLに遷移
      const params = new URLSearchParams(window.location.search);
      if (params.has('code') || params.has('state')) {
        // 認証パラメータがある場合は、保存されたURLに遷移
        const savedUrl = sessionStorage.getItem('auth_redirect_url');
        sessionStorage.removeItem('auth_redirect_url'); // 使用後は削除

        // 保存されたURLがあればそこに、なければ /iraiList に遷移
        const redirectTo = savedUrl || '/iraiList';
        navigate(redirectTo, { replace: true });
        return; // navigate後はこれ以上処理しない
      }

      refreshData();

      // 組織情報を取得
      const fetchOrganizationName = async () => {
        if (organizationId) {
          try {
            const { data: organizationData } = await apiGet<{ name?: string }>(`/organizations/${organizationId}`, accessToken);
            setOrganizationName(organizationData.name || '');
          } catch (error) {
            console.error('組織情報の取得でエラーが発生しました:', handleApiError(error, 'Fetching organization info'));
          }
        }
      };

      fetchOrganizationName();
    }
  }, [accessToken, auth.isAuthenticated, auth.isLoading, navigate, organizationId, profile, refreshData]);

  // ローディング中の表示
  if (auth.isLoading) {
    return <div>認証確認中...</div>;
  }

  // エラーがある場合の表示
  if (auth.error) {
    console.error('認証エラー:', auth.error);
    return <div>認証エラーが発生しました: {auth.error.message}</div>;
  }

  // 認証されていない場合の処理
  if (!auth.isAuthenticated) {
    // URLに認証関連のパラメータがある場合は、処理を待つ
    const params = new URLSearchParams(window.location.search);
    if (params.has('code') || params.has('state')) {
      return <div>認証処理中...</div>;
    }

    // 認証前に現在のURLを保存（認証後に復元するため）
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath && currentPath !== '/' && currentPath !== '/iraiList') {
      sessionStorage.setItem('auth_redirect_url', currentPath);
    }

    // 認証画面にリダイレクト
    auth.signinRedirect();
    return <div>認証画面に遷移中...</div>;
  }

  // 認証済みの場合、子コンポーネントを表示
  let userName: string = 'ユーザー';
  if (auth.user?.profile) {
    userName = auth.user.profile.name || auth.user.profile.email || 'ユーザー';
    setGlobalUserId(auth.user.profile.email || '');
  }

  return (
    <div style={{
      width: '100vw',
      minHeight: '100vh',
      margin: 0,
      padding: 15,
      boxSizing: 'border-box'
    }}>
      <div style={{
        width: '100%',
        padding: '2px',
        backgroundColor: '#f5f5f5',
        marginBottom: '2px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        fontSize: '12px',
        boxSizing: 'border-box'
      }}>
        {organizationName && (
          <span style={{ marginRight: '15px', fontWeight: 'bold' }}>
            {organizationName}{profile?.['custom:division'] ? ` ${profile['custom:division']}` : ''}
          </span>
        )}
        <span style={{ marginRight: '10px' }}>{userName}</span>
        <button
          onClick={signOutRedirect}
          style={{
            padding: '2px 8px',
            fontSize: '12px',
            cursor: 'pointer',
            border: 'none',
            background: 'transparent',
            color: '#007bff',
            textDecoration: 'underline'
          }}
        >
          サインアウト
        </button>
      </div>
      <div style={{
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {children}
      </div>
    </div>
  );
};

export default ProtectedRoute;
